import { useCallback, useEffect, useRef, useState } from "react";

import { apiErrorMessage } from "../services/utilities";
import { streamToSegments } from "../vendor/liveTapRenderer";
import { holdItems, knownBy, playStream } from "../liveTap/bandPlay";

// The band read's own bounds (platform band_read.MAX_TURNS / MAX_LIMIT): a
// cold page of turns, and a delta of items. Neither is a cap: a page says
// where the next one starts, a delta says when the limit cut it.
const PAGE_TURNS = 500;
const DELTA_LIMIT = 2000;
const DEFAULT_POLL_MS = 5000;
// A cassette in one of these states makes nothing more.
const DONE_STATES = new Set(["closed", "error"]);

// The read leg of the latency budget (TT-1103): how long after the cassette
// committed a pass this client saw it. Measured from its telemetry's
// ms-precise `updated_at` when a read brings a newer one; `null` between.
function seenLag(prevUpdatedAt, updatedAt) {
  if (!updatedAt || updatedAt === prevUpdatedAt) return null;
  const committed = Date.parse(updatedAt);
  return Number.isFinite(committed) ? Math.max(0, Date.now() - committed) : null;
}

// axios surfaces transient connectivity failures as a "Network Error" with no
// response object. These are non-fatal here — the next poll recovers — so
// they're logged rather than shown as a lingering red error banner.
function isNetworkError(err) {
  return err?.message === "Network Error" || (!!err?.request && !err?.response);
}

class ReadError extends Error {
  constructor(response, fallback) {
    super(apiErrorMessage(response, fallback));
    this.status = response?.status || null;
  }
}

function check(response, fallback) {
  if (!response || response.error) throw new ReadError(response, fallback);
  return response;
}

/**
 * The cassette a page follows: the one named, else the live cassette (a
 * meeting as the room saw it), else a batch cassette (a recording's
 * hindsight read), else the first the doc has.
 */
export function followedCassette(cassettes, csstId) {
  const all = cassettes || [];
  if (csstId) return all.find((c) => c.csst_id === csstId) || null;
  return all.find((c) => c.kind === "live") || all.find((c) => c.kind === "batch") || all[0] || null;
}

// The doc read's title and date, from the owner's read ({ doc: {...} }) or
// a live link's public one (top-level, camelized like every response).
function heading(read) {
  if (!read) return { title: "", createdAt: null };
  if (read.doc) return { title: read.doc.title || "", createdAt: read.doc.created_at || null };
  return { title: read.title || "", createdAt: read.createdAt || null };
}

/**
 * Follow one cassette of a spoken doc through the band read (TT-1273, the
 * platform's doc model, TT-1270): the doc read says what the doc has and
 * which cassette to follow; the cassette's sentences and marks come as one
 * cold read, paged through to the end, then as the `since` delta from the
 * last seq it carried, so a poll brings only what is new and nothing is
 * capped. The held items, linked by the read, play into the flat stream the
 * renderer regroups under the reveal rule (liveTap/bandPlay.js).
 *
 * `feed` is a live feed client (liveTap/liveFeedClient.js, TT-1110), memoized
 * by the caller on its target. Options:
 *   - `follow` (default true): hold the playhead at the cassette's newest
 *     `known` and keep reading. Off, the read stops at the frontier it found
 *     (T) and shows what was known by then — the mid-meeting tapestry;
 *   - `csstId`: a cassette other than the default (a replay, a batch read);
 *   - `telemetry`: re-read the doc each poll for the cassette's engine state
 *     (the workbench); otherwise the doc is read once and the band read
 *     carries the cassette's state.
 *
 * `status` is the cassette's state (running, closing, closed, error) and
 * `done` says it makes nothing more; the poll stops then. `errorStatus` is
 * the HTTP status of the last failing read (a viewer's 410 means the link is
 * gone).
 */
export default function useLiveTranscript(feed, options = {}) {
  const { autoRefresh = true, intervalMs = DEFAULT_POLL_MS, follow = true, csstId = null, telemetry = false } = options;
  const [read, setRead] = useState(null);
  const [cassette, setCassette] = useState(null);
  const [segments, setSegments] = useState([]);
  const [stream, setStream] = useState([]);
  const [status, setStatus] = useState(null);
  // ms from the last pass's commit to the read that showed it (read leg)
  const [seenLagMs, setSeenLagMs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [errorStatus, setErrorStatus] = useState(null);
  const requestIdRef = useRef(0);
  const inFlightRef = useRef(false);
  const loadedRef = useRef(false);
  // what the follower holds: the doc read, the cassette, its items by id,
  // where the next delta asks from, and the pinned T when not following
  const readRef = useRef(null);
  const cassetteRef = useRef(null);
  const heldRef = useRef(new Map());
  const lastSeqRef = useRef(null);
  const pinnedRef = useRef(null);
  const doneRef = useRef(false);
  const updatedAtRef = useRef(null);

  const readDoc = useCallback(async () => {
    const doc = check(await feed.getDoc(), "Unable to load the document");
    const picked = followedCassette(doc.cassettes, csstId);
    readRef.current = doc;
    cassetteRef.current = picked;
    setRead(doc);
    setCassette(picked);
    const lag = seenLag(updatedAtRef.current, picked?.telemetry?.updated_at);
    updatedAtRef.current = picked?.telemetry?.updated_at;
    if (lag != null) setSeenLagMs(lag);
    return picked;
  }, [csstId, feed]);

  // Every page of the cold read, from turn 0 until the band says it is
  // read through. The first page's `lastSeq` is the high-water before its
  // items: a follower that moves on from there misses nothing.
  const readCold = useCallback(
    async (csst) => {
      const items = [];
      let first = null;
      let fromTurn = 0;
      for (;;) {
        const page = check(
          await feed.getBands(csst, { bands: "sentences,marks", from_turn: fromTurn, turns: PAGE_TURNS }),
          "Unable to load the transcript"
        );
        first = first || page;
        items.push(...(page.items || []));
        const next = page.page?.next_turn;
        if (next == null || next <= fromTurn) break;
        fromTurn = next;
      }
      return { items, state: first.state, lastSeq: first.lastSeq, known: first.known };
    },
    [feed]
  );

  // The delta since the last seq held, asked again at once while the limit
  // cut it.
  const readDelta = useCallback(
    async (csst, since) => {
      const items = [];
      let state = null;
      let lastSeq = since;
      for (;;) {
        const got = check(
          await feed.getBands(csst, { bands: "sentences,marks", since: lastSeq, limit: DELTA_LIMIT }),
          "Unable to load the transcript"
        );
        state = got.state;
        items.push(...(got.items || []));
        lastSeq = got.delta?.last_seq ?? got.lastSeq ?? lastSeq;
        if (!got.delta?.more) break;
      }
      return { items, state, lastSeq };
    },
    [feed]
  );

  const refresh = useCallback(async () => {
    if (!feed || inFlightRef.current) return;
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    inFlightRef.current = true;
    setRefreshing(true);
    if (!loadedRef.current) setLoading(true);

    try {
      const picked = !readRef.current || telemetry ? await readDoc() : cassetteRef.current;
      if (requestId !== requestIdRef.current) return;
      if (!picked) {
        // none yet (a request just made, a backfill not run): the next poll
        // reads the doc again rather than ending here
        readRef.current = null;
        setStatus(null);
        throw new ReadError({ msg: "This document has nothing to play yet" });
      }
      const spine = picked.processing === "spine";
      let got;
      // a batch cassette has no delta: it is read whole while it runs
      if (spine && lastSeqRef.current != null) {
        got = await readDelta(picked.csst_id, lastSeqRef.current);
      } else {
        got = await readCold(picked.csst_id);
        if (!follow) pinnedRef.current = got.known ?? null;
      }
      if (requestId !== requestIdRef.current) return;
      if (spine) lastSeqRef.current = got.lastSeq ?? lastSeqRef.current;
      const nextHeld = holdItems(heldRef.current, got.items);
      if (nextHeld !== heldRef.current) {
        heldRef.current = nextHeld;
        const played = playStream(knownBy([...nextHeld.values()], follow ? null : pinnedRef.current));
        setStream(played);
        setSegments(streamToSegments(played));
      }
      doneRef.current = !follow || DONE_STATES.has(got.state);
      setStatus(got.state || null);
      setError("");
      setErrorStatus(null);
      loadedRef.current = true;
    } catch (err) {
      if (requestId === requestIdRef.current) {
        if (isNetworkError(err)) {
          console.log("[useLiveTranscript] network error (will retry on next poll):", err.message);
          setError("");
        } else {
          setErrorStatus(err.status || null);
          setError(err.message || "Unable to load the transcript");
        }
      }
    } finally {
      if (requestId === requestIdRef.current) {
        inFlightRef.current = false;
        setLoading(false);
        setRefreshing(false);
      }
    }
  }, [feed, follow, readCold, readDelta, readDoc, telemetry]);

  useEffect(() => {
    // a read still in flight for the previous target lands as stale
    requestIdRef.current += 1;
    inFlightRef.current = false;
    loadedRef.current = false;
    readRef.current = null;
    cassetteRef.current = null;
    heldRef.current = new Map();
    lastSeqRef.current = null;
    pinnedRef.current = null;
    doneRef.current = false;
    updatedAtRef.current = null;
    setRead(null);
    setCassette(null);
    setSegments([]);
    setStream([]);
    setStatus(null);
    setSeenLagMs(null);
    setError("");
    setErrorStatus(null);
    setLoading(!!feed);
  }, [feed, csstId, follow]);

  useEffect(() => {
    if (!feed) return undefined;
    refresh();
    if (!autoRefresh) return undefined;
    const intervalId = setInterval(() => {
      if (!doneRef.current) refresh();
    }, intervalMs);
    return () => clearInterval(intervalId);
  }, [autoRefresh, feed, intervalMs, refresh]);

  const { title, createdAt } = heading(read);
  return {
    read,
    title,
    createdAt,
    cassette,
    segments,
    stream,
    telemetry: cassette?.telemetry || null,
    status,
    done: DONE_STATES.has(status),
    seenLagMs,
    loading,
    refreshing,
    error,
    errorStatus,
    refresh,
  };
}
